/** Port Consumption Database — filter API normalization & Transit/Bunkering merge. */

import { dedupePortDatabaseSegments } from './port-database-segments.util';
import { dedupeFilterOptions, normalizeFilterOptionKey } from './port-database-table-filter.util';

export const TRANSIT_FILTER_LABEL = 'Transit';
const DEBURKING_BUNKERING_PATTERN = /\b(?:de[-_\s]*)?(?:bunk|burk)(?:er)?(?:ing)?\b/i;
const TRANSIT_PATTERN = /\btransit\b/i;

export interface PortConsumptionFilterOptionsPayload {
  username: string;
  /** Empty string when Active + Inactive (or none) are selected together. */
  client_status: string;
  /**
   * Selected clients as an array.
   * - `null` when user clicks Select All
   * - `[]` when nothing is selected (no client constraint)
   * - selected values for a partial selection
   */
  client: string[] | null;
  segment: string;
  vessel_names: string[];
  port_types: string[];
  port_names: string[];
  operations: string[];
  voyage_statuses: string[];
  from_date: string;
  to_date: string;
  over_consumption: string[];
}

export interface PortDatabaseFilterOptionsResponse {
  client?: string | string[];
  clients?: string[];
  operations?: string[];
  over_consumption?: string[];
  port_names?: string[];
  port_types?: string[];
  segments?: string[];
  ship_names?: string[];
  voyage_statuses?: string[];
  status?: string;
}

export interface NormalizedPortDatabaseFilterOptions {
  clients: string[];
  operations: string[];
  overConsumption: string[];
  portNames: string[];
  portTypes: string[];
  segments: string[];
  shipNames: string[];
  voyageStatuses: string[];
}

/** Case-insensitive client dedupe — keeps the cleanest display label. */
export function normalizePortDatabaseClients(clients: string[]): string[] {
  const byKey = new Map<string, string>();

  for (const raw of clients || []) {
    const trimmed = String(raw ?? '').replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, trimmed);
      continue;
    }
    const prefer = scoreClientLabel(trimmed) >= scoreClientLabel(existing) ? trimmed : existing;
    byKey.set(key, prefer);
  }

  return Array.from(byKey.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

function scoreClientLabel(label: string): number {
  let score = 0;
  if (/[a-z]/.test(label) && /[A-Z]/.test(label)) {
    score += 3;
  }
  if (label === label.toUpperCase()) {
    score -= 1;
  }
  score += label.length / 100;
  return score;
}

export function isDeburkingOrBunkeringOperation(value: string): boolean {
  return DEBURKING_BUNKERING_PATTERN.test(String(value ?? ''));
}

export function isBunkeringOperation(value: string): boolean {
  return isDeburkingOrBunkeringOperation(value);
}

export function isTransitOperation(value: string): boolean {
  return TRANSIT_PATTERN.test(String(value ?? ''));
}

export const PORT_DATABASE_OPERATIONS: string[] = [
  'Discharging',
  'Loading',
  'Transit',
];

/** Hide Deburking / Debunkering / Bunkering as separate options; keep Transit as the combined label. */
export function normalizeOperationsFilterOptions(values: string[]): string[] {
  const deduped = dedupeFilterOptions(values || []);
  const result = new Set<string>();

  for (const value of deduped) {
    if (isTransitOperation(value) || isDeburkingOrBunkeringOperation(value)) {
      result.add(TRANSIT_FILTER_LABEL);
    } else if (/^loading$/i.test(String(value ?? '').trim())) {
      result.add('Loading');
    } else if (/^discharging$/i.test(String(value ?? '').trim())) {
      result.add('Discharging');
    } else if (!isDeburkingOrBunkeringOperation(value)) {
      result.add(value);
    }
  }

  return Array.from(result).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

export function normalizeSegmentFilterOptions(values: string[]): string[] {
  if (!values?.length) {
    return [];
  }
  return dedupePortDatabaseSegments(values);
}

export function normalizeFilterApiOptions(
  response: PortDatabaseFilterOptionsResponse | null | undefined,
): NormalizedPortDatabaseFilterOptions {
  const clientsFromList = response?.clients ?? [];
  const clientField = response?.client;
  const clientsFromField = Array.isArray(clientField)
    ? clientField
    : clientField
      ? [clientField]
      : [];

  // Backend may send `client: string[]` (preferred) and/or `clients: string[]`.
  const mergedClients =
    clientsFromField.length >= clientsFromList.length
      ? clientsFromField.length
        ? clientsFromField
        : clientsFromList
      : clientsFromList.length
        ? clientsFromList
        : clientsFromField;

  return {
    clients: normalizePortDatabaseClients(mergedClients),
    operations: normalizeOperationsFilterOptions(response?.operations ?? []),
    overConsumption: dedupeFilterOptions(response?.over_consumption ?? []),
    portNames: dedupeFilterOptions(response?.port_names ?? []),
    portTypes: dedupeFilterOptions(response?.port_types ?? []),
    segments: normalizeSegmentFilterOptions(response?.segments ?? []),
    shipNames: dedupeFilterOptions(response?.ship_names ?? []),
    voyageStatuses: dedupeFilterOptions(response?.voyage_statuses ?? []),
  };
}

export function matchesDatabaseOperationFilter(
  itemOperation: string | null | undefined,
  selectedOperations: string[],
): boolean {
  if (!selectedOperations?.length) {
    return true;
  }
  const operation = String(itemOperation ?? '').trim().toLowerCase();
  if (!operation) {
    return false;
  }

  return selectedOperations.some((selected) => {
    const normalized = String(selected ?? '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    if (normalized === TRANSIT_FILTER_LABEL.toLowerCase()) {
      return isTransitOperation(operation) || isDeburkingOrBunkeringOperation(operation);
    }
    return normalizeFilterOptionKey(operation) === normalizeFilterOptionKey(normalized);
  });
}

/**
 * Keep still-valid selections and map them to the canonical option value
 * from the latest API response (handles MR/mr, HANNA/Hanna duplicates).
 */
export function pruneSelectionsToOptions(
  selected: string[],
  options: string[],
): string[] {
  const byKey = new Map(
    (options || []).map((value) => [normalizeFilterOptionKey(value), value]),
  );
  const result: string[] = [];
  const seen = new Set<string>();

  for (const value of selected || []) {
    const key = normalizeFilterOptionKey(value);
    if (!key || seen.has(key)) {
      continue;
    }
    const canonical = byKey.get(key);
    if (!canonical) {
      continue;
    }
    seen.add(key);
    result.push(canonical);
  }

  return result;
}

export function formatFilterOptionsDate(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === '') {
    return '';
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return '';
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return String(value).trim();
}

export function normalizeClientStatusValue(
  value: string | null | undefined,
): 'Active' | 'Inactive' {
  return String(value ?? '').trim().toLowerCase() === 'inactive'
    ? 'Inactive'
    : 'Active';
}

function sanitizePayloadOptionValues(values: string[] | null | undefined): string[] {
  return (values || []).filter(
    (value) =>
      !!String(value ?? '').trim() &&
      String(value).trim() !== 'No options available',
  );
}

/** True when every available option is currently selected (Select All). */
export function isAllFilterOptionsSelected(
  selected: string[] | null | undefined,
  options: string[] | null | undefined,
): boolean {
  const selectedClean = sanitizePayloadOptionValues(selected);
  const optionClean = sanitizePayloadOptionValues(options);
  if (!selectedClean.length || !optionClean.length) {
    return false;
  }
  const selectedKeys = new Set(
    selectedClean.map((value) => normalizeFilterOptionKey(value)),
  );
  return optionClean.every((opt) =>
    selectedKeys.has(normalizeFilterOptionKey(opt)),
  );
}

/**
 * Multi-select cascade values for filter-options.
 * Select All / empty → `[]` (no constraint in payload).
 */
export function resolveMultiSelectFilterPayload(
  selected: string[] | null | undefined,
  options: string[] | null | undefined,
): string[] {
  const selectedClean = sanitizePayloadOptionValues(selected);
  if (!selectedClean.length) {
    return [];
  }
  if (isAllFilterOptionsSelected(selectedClean, options)) {
    return [];
  }
  return selectedClean;
}

/**
 * Client field for filter-options.
 * Select All → `null`; empty → `[]`; otherwise the selected subset.
 */
export function resolveClientFilterPayload(
  selected: string[] | null | undefined,
  options: string[] | null | undefined,
): string[] | null {
  const selectedClean = sanitizePayloadOptionValues(selected);
  if (!selectedClean.length) {
    return [];
  }
  if (isAllFilterOptionsSelected(selectedClean, options)) {
    return null;
  }
  return selectedClean;
}

/**
 * Multi-select cascade values for operations in filter-options payload.
 * When Transit is selected, sends Transit along with Debunkering / Deburking variants for API compatibility.
 */
export function resolveOperationsFilterPayload(
  selected: string[] | null | undefined,
  options: string[] | null | undefined,
): string[] {
  const base = resolveMultiSelectFilterPayload(selected, options);
  if (!base.length) {
    return [];
  }
  const hasTransit = base.some((val) => isTransitOperation(val));
  if (!hasTransit) {
    return base;
  }
  const expanded = new Set(base);
  expanded.add('Transit');
  expanded.add('Debunkering');
  expanded.add('Deburking');
  expanded.add('Bunkering');
  return Array.from(expanded);
}

